import * as React from "react";

import * as Cmp from "../../utils/comparison";
import * as DV from "../../utils/dataView";

interface FormattedView<ITEM> {
    dataView: DV.DataView<ITEM>;
    columns: Array<SimpleColumn<ITEM>>;
}

type ColumnBuilder<ITEM, VAL> = (
    extractor: DV.DataExtractor<ITEM, VAL>
) => ColumnOptions<ITEM>;

interface FixedWidthLayout {
    fixedWidth: number;
}

type Layout = FixedWidthLayout;

type Display = "ALWAYS" | "DEFAULT_VISIBLE" | "DEFAULT_HIDDEN" | "NEVER";

type CellClassName<ITEM> =
    | {
          type: "Static";
          value: string;
      }
    | {
          type: "Dynamic";
          get: (item: ITEM, rowIndex: number) => string | undefined;
      };

interface ColumnOptions<ITEM> {
    getFormattedValue?: (item: ITEM) => React.ReactNode;
    getCopyableValue?: (item: ITEM) => string | number | undefined;
    layout?: Layout;
    cellClassName?: CellClassName<ITEM>;
    display?: Display;
    order?: number;
    sortable?: boolean;
}

interface SimpleColumn<ITEM> {
    extractor: DV.DataExtractor<ITEM, any>;
    getFormattedValue: (item: ITEM) => React.ReactNode;
    getCopyableValue: (item: ITEM) => string | number | undefined;
    layout: Layout | undefined;
    cellClassName: CellClassName<ITEM> | undefined;
    display: Display;
    order: number | undefined;
    sortable: boolean;
}

function toColumn<ITEM, VAL>(
    extractor: DV.DataExtractor<ITEM, VAL>,
    options?: ColumnOptions<ITEM>
): SimpleColumn<ITEM> {
    const {
        getFormattedValue = (item: ITEM) => extractor.asString(item),
        getCopyableValue = (item: ITEM) => extractor.asString(item),
        layout,
        cellClassName,
        display = "DEFAULT_VISIBLE",
        order,
        sortable,
    } = options ?? {};
    return {
        extractor,
        getFormattedValue,
        getCopyableValue,
        layout,
        cellClassName,
        display,
        order,
        sortable: sortable !== undefined ? sortable : true,
    };
}

function sortColumns<ITEM>(columns: Array<SimpleColumn<ITEM>>) {
    const positions: { [colId: string]: number } = {};
    columns.forEach((col, pos) => {
        positions[col.extractor.id] = pos;
    });
    columns.sort(
        Cmp.chain([
            Cmp.map((col) => col.order, Cmp.undefinedLast()),
            Cmp.map((col) => positions[col.extractor.id]),
        ])
    );
}

export { toColumn, sortColumns };

export type {
    FormattedView,
    ColumnBuilder,
    FixedWidthLayout,
    Layout,
    Display,
    SimpleColumn,
    CellClassName,
    ColumnOptions,
};
